// Xuất bảng lương ra Excel (mỗi mẫu một sheet) và file mẫu nhập công. Bảng dựng bằng
// core/payrollSheet giống bản in, nên file Excel và tờ ký nhận luôn cùng cột, cùng tổng.
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { buildPayrollSheetModel } from '../core/payrollSheet';

const ACCENT_STYLE = {
    alert: { font: { color: { argb: 'FFC00000' } } },
    highlight: { fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } } },
    total: { font: { bold: true } },
    net: { font: { bold: true }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2EFDA' } } },
};
const BORDER = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };

/** Mô hình bảng lương từ các nhóm của ma trận (đã là số chốt hoặc số đang tính). */
export function sheetModelFromGroups(period, groups) {
    return buildPayrollSheetModel({
        period: `${String(period.THANG).padStart(2, '0')}/${period.NAM}`,
        templates: groups.map((group) => ({
            code: group.templateId,
            name: group.templateName,
            columns: group.columns.map((column, index) => ({ code: column.code, name: column.name, dataType: column.dataType, order: index })),
        })),
        employees: groups.flatMap((group) => group.rows.map((row) => ({
            soHd: row.soHd,
            hoTen: row.hoTen,
            to: row.to,
            templateCode: group.templateId,
            templateName: group.templateName,
            values: Object.fromEntries(Object.entries(row.cells).map(([code, cell]) => [code, cell.value === null || cell.value === '' ? null : Number(cell.value)])),
        }))),
    });
}

const safeSheetName = (name, used) => {
    let base = String(name).replace(/[\\/?*[\]:]/g, ' ').slice(0, 28) || 'Bang luong';
    let candidate = base;
    for (let index = 2; used.has(candidate); index += 1) candidate = `${base.slice(0, 26)} ${index}`;
    used.add(candidate);
    return candidate;
};

export async function exportPayrollWorkbook(period, groups, { draft }) {
    const { default: ExcelJS } = await import('exceljs');
    const model = sheetModelFromGroups(period, groups);
    const workbook = new ExcelJS.Workbook();
    const usedNames = new Set();
    for (const sheet of model.sheets) {
        const worksheet = workbook.addWorksheet(safeSheetName(sheet.templateName, usedNames), { views: [{ state: 'frozen', xSplit: 3, ySplit: 4 }] });
        const lastColumn = 4 + sheet.columns.length;
        worksheet.mergeCells(1, 1, 1, lastColumn);
        worksheet.getCell(1, 1).value = `${model.title}${draft ? ' (BẢN NHÁP - CHƯA DUYỆT)' : ''}`;
        worksheet.getCell(1, 1).font = { bold: true, size: 14 };
        worksheet.getCell(1, 1).alignment = { horizontal: 'center' };
        worksheet.mergeCells(2, 1, 2, lastColumn);
        worksheet.getCell(2, 1).value = `Mẫu: ${sheet.templateName} — Mã kỳ ${period.MA_KY}`;
        worksheet.getCell(2, 1).alignment = { horizontal: 'center' };

        const header = ['STT', 'HỌ VÀ TÊN', 'TỔ', ...sheet.columns.map((column) => column.header), 'KÝ NHẬN'];
        const headerRow = worksheet.getRow(4);
        header.forEach((title, index) => {
            const cell = headerRow.getCell(index + 1);
            cell.value = title;
            cell.font = { bold: true };
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            cell.border = BORDER;
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDCE6F1' } };
        });
        headerRow.height = 36;
        worksheet.getColumn(1).width = 6;
        worksheet.getColumn(2).width = 26;
        worksheet.getColumn(3).width = 12;
        sheet.columns.forEach((column, index) => { worksheet.getColumn(4 + index).width = column.width; });
        worksheet.getColumn(lastColumn).width = 14;

        sheet.rows.forEach((row, rowIndex) => {
            const excelRow = worksheet.getRow(5 + rowIndex);
            excelRow.getCell(1).value = row.stt;
            excelRow.getCell(2).value = row.hoTen;
            excelRow.getCell(3).value = row.to;
            sheet.columns.forEach((column, index) => {
                const cell = excelRow.getCell(4 + index);
                const value = row.values[column.code];
                cell.value = value === null || value === 0 ? null : value;
                cell.numFmt = column.decimals ? `#,##0.${'0'.repeat(column.decimals)}` : '#,##0';
                Object.assign(cell, ACCENT_STYLE[column.accent] ?? {});
            });
            for (let col = 1; col <= lastColumn; col += 1) excelRow.getCell(col).border = BORDER;
        });
        for (const span of sheet.teamSpans) {
            if (span.length > 1) worksheet.mergeCells(5 + span.start, 3, 5 + span.start + span.length - 1, 3);
            worksheet.getCell(5 + span.start, 3).alignment = { vertical: 'middle', horizontal: 'center' };
        }
        const totalRow = worksheet.getRow(5 + sheet.rows.length);
        worksheet.mergeCells(totalRow.number, 1, totalRow.number, 3);
        totalRow.getCell(1).value = 'TỔNG CỘNG';
        totalRow.getCell(1).alignment = { horizontal: 'center' };
        sheet.columns.forEach((column, index) => {
            const cell = totalRow.getCell(4 + index);
            cell.value = sheet.totals[column.code];
            cell.numFmt = column.decimals ? `#,##0.${'0'.repeat(column.decimals)}` : '#,##0';
        });
        for (let col = 1; col <= lastColumn; col += 1) {
            totalRow.getCell(col).font = { bold: true };
            totalRow.getCell(col).border = BORDER;
        }
        const signRow = totalRow.number + 2;
        [['NGƯỜI LẬP BẢNG', 2], ['KẾ TOÁN', Math.max(4, Math.floor(lastColumn / 2))], ['GIÁM ĐỐC', lastColumn - 1]].forEach(([title, col]) => {
            worksheet.getCell(signRow, col).value = title;
            worksheet.getCell(signRow, col).font = { bold: true };
        });
    }
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `BangLuong_${period.MA_KY}${draft ? '_nhap' : ''}.xlsx`);
}

/** File mẫu nhập công: mỗi người một dòng, cột là các biến được nhập của mẫu, điền sẵn số đã có. */
export function downloadInputTemplate(period, groups) {
    const codes = [...new Set(groups.flatMap((group) => group.columns.filter((column) => column.allowInput).map((column) => column.code)))];
    const names = new Map(groups.flatMap((group) => group.columns.map((column) => [column.code, column.name])));
    const rows = groups.flatMap((group) => group.rows.map((row) => [
        row.soHd, row.hoTen, row.to, group.templateName,
        ...codes.map((code) => (row.cells[code] && row.cells[code].value !== null ? row.cells[code].value : '')),
    ]));
    const sheet = XLSX.utils.aoa_to_sheet([['SỐ HĐ', 'HỌ TÊN', 'TỔ', 'MẪU', ...codes], ...rows]);
    sheet['!cols'] = [{ wch: 10 }, { wch: 26 }, { wch: 12 }, { wch: 20 }, ...codes.map(() => ({ wch: 10 }))];
    const legend = XLSX.utils.aoa_to_sheet([['MÃ', 'Ý NGHĨA'], ...codes.map((code) => [code, names.get(code) || code]), [], ['Ghi chú', 'Số lẻ gõ 1,5 hoặc 1.5. Ô của biến không thuộc mẫu người đó sẽ bị bỏ qua.']]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'Nhập công');
    XLSX.utils.book_append_sheet(workbook, legend, 'Giải thích');
    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `NhapCong_${period.MA_KY}.xlsx`);
}

export function readSheetRows(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const workbook = XLSX.read(event.target.result, { type: 'array' });
                resolve(XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], { defval: '', raw: true }));
            } catch (error) {
                reject(error);
            }
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(file);
    });
}

/** Báo cáo công lương ra Excel theo đúng cách xem đang chọn, có dòng tổng. */
export async function exportPayrollReport({ title, groupBy, rows, totals, amounts }) {
    const { default: ExcelJS } = await import('exceljs');
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Báo cáo', { views: [{ state: 'frozen', ySplit: 3 }] });
    const leading = {
        detail: [['KỲ', 10, (row) => row.periodLabel], ['SỐ HĐ', 10, (row) => row.soHd], ['HỌ VÀ TÊN', 26, (row) => row.hoTen], ['TỔ', 14, (row) => row.to]],
        month: [['THÁNG', 12, (row) => row.label.replace('Tháng ', '')], ['MÃ KỲ', 14, (row) => row.maKy], ['SỐ NHÂN SỰ', 12, (row) => row.employeeCount]],
        team: [['TỔ', 22, (row) => row.label], ['SỐ NHÂN SỰ', 12, (row) => row.employeeCount], ['SỐ KỲ', 8, (row) => row.periodCount]],
        employee: [['SỐ HĐ', 10, (row) => row.soHd], ['HỌ VÀ TÊN', 26, (row) => row.label], ['TỔ', 14, (row) => row.to], ['SỐ KỲ', 8, (row) => row.periodCount]],
    }[groupBy];
    const lastColumn = 1 + leading.length + amounts.length;
    worksheet.mergeCells(1, 1, 1, lastColumn);
    worksheet.getCell(1, 1).value = title;
    worksheet.getCell(1, 1).font = { bold: true, size: 14 };
    worksheet.getCell(1, 1).alignment = { horizontal: 'center' };

    const headerRow = worksheet.getRow(3);
    ['STT', ...leading.map(([header]) => header), ...amounts.map((amount) => amount.label.toUpperCase())].forEach((header, index) => {
        const cell = headerRow.getCell(index + 1);
        cell.value = header;
        cell.font = { bold: true };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.border = BORDER;
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDCE6F1' } };
    });
    worksheet.getColumn(1).width = 6;
    leading.forEach(([, width], index) => { worksheet.getColumn(2 + index).width = width; });
    amounts.forEach((_, index) => { worksheet.getColumn(2 + leading.length + index).width = 16; });

    const writeAmounts = (excelRow, source) => amounts.forEach((amount, index) => {
        const cell = excelRow.getCell(2 + leading.length + index);
        cell.value = source[amount.key] || 0;
        cell.numFmt = '#,##0';
    });
    rows.forEach((row, rowIndex) => {
        const excelRow = worksheet.getRow(4 + rowIndex);
        excelRow.getCell(1).value = rowIndex + 1;
        leading.forEach(([, , get], index) => { excelRow.getCell(2 + index).value = get(row); });
        writeAmounts(excelRow, row);
        for (let col = 1; col <= lastColumn; col += 1) excelRow.getCell(col).border = BORDER;
    });
    const totalRow = worksheet.getRow(4 + rows.length);
    worksheet.mergeCells(totalRow.number, 1, totalRow.number, 1 + leading.length);
    totalRow.getCell(1).value = 'TỔNG CỘNG';
    totalRow.getCell(1).alignment = { horizontal: 'center' };
    writeAmounts(totalRow, totals);
    for (let col = 1; col <= lastColumn; col += 1) {
        totalRow.getCell(col).font = { bold: true };
        totalRow.getCell(col).border = BORDER;
    }
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `${title.replace(/[\/:*?"<>|]/g, '-')}.xlsx`);
}
